import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Route, Routes, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useStore, useDeliveryFacts } from "./StoreContext";
import { matchesQuery, priceInfo } from "./shopData";
import { money } from "./format";
import { CountdownShort } from "./Countdown";
import ShopHome from "./ShopHome";
import ProductPage from "./ProductPage";
import CartPage from "./CartPage";
import CheckoutPage from "./CheckoutPage";
import Icon from "./Icons";
import { contentOf, isExternal } from "./storeContent";

export default function StoreShell() {
  const location = useLocation();
  const nav = useNavigationType();
  const { config } = useStore();

  // New page → top; back/forward keeps the browser's own scroll position.
  useEffect(() => {
    if (nav !== "POP") window.scrollTo(0, 0);
  }, [location.pathname, nav]);

  return (
    <div className="shop" data-display={contentOf(config).displayFont}>
      {config.previewing && <div className="sh-preview">Preview. The store isn't public yet</div>}
      <Announcement />
      <StoreBar />
      <main className="sh-main">
        <Routes>
          <Route path="/merch" element={<ShopHome />} />
          <Route path="/merch/collection/:slug" element={<ShopHome />} />
          <Route path="/merch/cart" element={<CartPage />} />
          <Route path="/merch/checkout" element={<CheckoutPage />} />
          <Route path="/merch/:slug" element={<ProductPage />} />
          <Route path="*" element={<ShopHome />} />
        </Routes>
      </main>
      <AddedToast />
    </div>
  );
}

// One quiet line above the store: what the CMS says, or by default the most
// useful true thing right now.
function Announcement() {
  const { collections, sales, refresh, config } = useStore();
  const d = useDeliveryFacts();
  const note = contentOf(config).announcement;
  if (note.mode === "off") return null;
  if (note.mode === "custom" && note.text) {
    const text = note.href ? (isExternal(note.href) ? <a href={note.href} target="_blank" rel="noreferrer">{note.text}</a> : <Link to={note.href}>{note.text}</Link>) : <span>{note.text}</span>;
    return <div className="sh-announce">{text}</div>;
  }
  const next = collections.filter((c) => c.teased).sort((a, b) => String(a.launchAt).localeCompare(String(b.launchAt)))[0];
  const sale = sales.find((s) => s.endsAt);
  let content = null;
  if (next) {
    content = (
      <Link to={`/merch/collection/${next.slug}`}>
        {next.name} drops in <CountdownShort to={next.launchAt} onDone={refresh} />
      </Link>
    );
  } else if (sale) {
    content = (
      <span>
        {sale.name} ends in <CountdownShort to={sale.endsAt} onDone={refresh} />
      </span>
    );
  } else if (d.freeOverMinor != null) {
    content = <span>Free delivery in Czechia over {money(d.freeOverMinor)}</span>;
  } else {
    content = <span>Shipped from Prague across the EU · {d.returnDays}-day returns</span>;
  }
  return <div className="sh-announce">{content}</div>;
}

function StoreBar() {
  const { count } = useStore();
  const [searching, setSearching] = useState(false);
  const location = useLocation();
  const checkout = location.pathname.startsWith("/merch/checkout");
  useEffect(() => setSearching(false), [location.pathname]);
  return (
    <div className={`sh-bar${searching ? " searching" : ""}`}>
      <div className="sh-wrap sh-bar-inner">
        <Link to="/merch" className="sh-bar-home">
          Merch
        </Link>
        {checkout ? (
          <span className="sh-bar-secure">
            <Icon name="lock" size={14} /> Secure checkout
          </span>
        ) : (
          <div className="sh-bar-end">
            <button className="sh-iconbtn" onClick={() => setSearching(true)} aria-label="Search the store">
              <Icon name="search" />
            </button>
            <Link to="/merch/cart" className="sh-iconbtn sh-cartbtn" aria-label={`Cart, ${count} ${count === 1 ? "item" : "items"}`}>
              <Icon name="bag" />
              {count > 0 && <span className="sh-cartcount">{count}</span>}
            </Link>
          </div>
        )}
      </div>
      {searching && <SearchPanel onClose={() => setSearching(false)} />}
    </div>
  );
}

function SearchPanel({ onClose }) {
  const { products } = useStore();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const input = useRef(null);
  useEffect(() => {
    input.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const hits = useMemo(() => (q.trim() ? products.filter((p) => matchesQuery(p, q)).slice(0, 6) : []), [products, q]);
  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/merch?q=${encodeURIComponent(q.trim())}`);
    onClose();
  };
  return (
    <>
      <div className="sh-search-scrim" onMouseDown={onClose} />
      <div className="sh-search" role="search">
        <form className="sh-wrap sh-search-form" onSubmit={submit}>
          <Icon name="search" />
          <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search merch" aria-label="Search merch" enterKeyHint="search" />
          <button type="button" className="sh-iconbtn" onClick={onClose} aria-label="Close search">
            <Icon name="close" />
          </button>
        </form>
        {q.trim() && (
          <div className="sh-wrap sh-search-results">
            {hits.length ? (
              <>
                <ul>
                  {hits.map((p) => (
                    <li key={p.id}>
                      <Link to={`/merch/${p.slug}`} onClick={onClose}>
                        <span className="sh-search-img">{p.images[0] && <img src={p.images[0].url} alt="" />}</span>
                        <span className="sh-search-title">{p.title}</span>
                        <span className="sh-muted">{money(priceInfo(p).min)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <button className="sh-textlink" onClick={submit}>
                  See all results <Icon name="arrow" size={14} />
                </button>
              </>
            ) : (
              <p className="sh-muted">Nothing for “{q}”. Try “shirt” or “vinyl”.</p>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// "Added to cart" — stays out of the way, offers the two next steps.
function AddedToast() {
  const { toast, dismissToast, count } = useStore();
  const location = useLocation();
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(dismissToast, 6000);
    return () => clearTimeout(t);
  }, [toast, dismissToast]);
  useEffect(() => {
    dismissToast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  if (!toast) return null;
  const { product, variant } = toast;
  const opts = Object.values(variant.optionValues || {}).filter(Boolean).join(" / ");
  const img = (product.images.find((i) => i.variantId === variant.id) || product.images[0])?.url;
  return (
    <div className="sh-toast" role="status" aria-live="polite" key={toast.key}>
      <div className="sh-toast-head">
        <span>
          <Icon name="check" size={16} /> Added to cart
        </span>
        <button className="sh-iconbtn" onClick={dismissToast} aria-label="Close">
          <Icon name="close" size={18} />
        </button>
      </div>
      <div className="sh-toast-item">
        {img && <img src={img} alt="" />}
        <div>
          <strong>{product.title}</strong>
          {opts && <span>{opts}</span>}
          <span>{money(variant.priceMinor)}</span>
        </div>
      </div>
      <div className="sh-toast-actions">
        <Link className="sh-btn sh-btn-ghost" to="/merch/cart">
          Cart ({count})
        </Link>
        <Link className="sh-btn" to="/merch/checkout">
          Checkout
        </Link>
      </div>
    </div>
  );
}
